import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { SectionCard, Pill, ActionButton, EmptyState, LoadingState, AlertBanner } from "@/components/shared";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";

const PROGRESS_LABELS = {
  questionnaire: "Questionnaire",
  documents: "Documents",
  supplemental: "Supplemental Rounds",
  permanent_tax_file: "Permanent Tax File",
};
const BLOCK_ICON = {
  high_findings: "AlertTriangle", supplemental: "MessageSquareWarning",
  documents: "Paperclip", ptf: "Landmark", open_round: "Send",
};
// Colored accents for the four governance-critical report buckets.
const SECTION_ACCENT = {
  facts: "border-l-emerald-400", assumptions: "border-l-amber-400",
  planning_opportunities: "border-l-blue-400", recommended_actions: "border-l-blue-400",
  open_items: "border-l-rose-400",
};

function Bar({ k, pct }) {
  const tone = pct >= 100 ? "bg-emerald-500" : pct >= 50 ? "bg-amber-500" : "bg-rose-500";
  return (
    <div data-testid={`sr-progress-${k}`}>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-slate-600">{PROGRESS_LABELS[k] || k}</span>
        <span className="text-slate-400">{pct}%</span>
      </div>
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full ${tone} transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function ReadinessReport({ assignments, clientId, onGoToTab }) {
  const navigate = useNavigate();
  const published = (assignments || []).find((a) => ["portal_ready", "in_progress", "submitted"].includes(a.status));
  const [loading, setLoading] = useState(true);
  const [completion, setCompletion] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [report, setReport] = useState(null);
  const [basis, setBasis] = useState(null);
  const [assumptions, setAssumptions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [savingSections, setSavingSections] = useState(false);
  const [aText, setAText] = useState("");
  const [aSource, setASource] = useState("");
  const reportRef = useRef(null);
  const pollRef = useRef(null);

  const loadAll = useCallback(async () => {
    if (!published) { setLoading(false); return; }
    try {
      const { data: c } = await api.get(`/strategic-review/assignments/${published.id}/completion`);
      const comp = c.completion;
      setCompletion(comp);
      if (comp) {
        const [rd, rp, bs, as] = await Promise.all([
          api.get(`/strategic-reviews/completion/${comp.id}/readiness`),
          api.get(`/strategic-reviews/completion/${comp.id}/report`),
          api.get(`/strategic-reviews/completion/${comp.id}/report-basis`),
          api.get(`/strategic-reviews/completion/${comp.id}/assumptions`),
        ]);
        setReadiness(rd.data);
        setReport(rp.data.report || null);
        setBasis(bs.data);
        setAssumptions(as.data.assumptions || []);
        if (rp.data.report?.status === "drafting") schedulePoll(comp.id);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load readiness");
    } finally {
      setLoading(false);
    }
  }, [published]);
  useEffect(() => { loadAll(); return () => pollRef.current && clearTimeout(pollRef.current); }, [loadAll]);

  const schedulePoll = (cid) => {
    if (pollRef.current) clearTimeout(pollRef.current);
    pollRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/strategic-reviews/completion/${cid}/report`);
        setReport(data.report || null);
        if (data.report?.status === "drafting") schedulePoll(cid);
        else { refreshReadiness(); toast.success("Allison finished drafting the report"); }
      } catch { schedulePoll(cid); }
    }, 6000);
  };

  const refreshReadiness = useCallback(async () => {
    if (!completion) return;
    try {
      const [rd, bs] = await Promise.all([
        api.get(`/strategic-reviews/completion/${completion.id}/readiness`),
        api.get(`/strategic-reviews/completion/${completion.id}/report-basis`),
      ]);
      setReadiness(rd.data); setBasis(bs.data);
    } catch { /* ignore */ }
  }, [completion]);

  const actOnBlock = (b) => {
    if (b.action === "findings") { onGoToTab?.("findings"); toast.info("Resolve the high-severity findings in the Allison Findings tab"); }
    else if (b.action === "round") { onGoToTab?.("findings"); toast.info("Manage the open round in Allison Findings → Review Package"); }
    else if (b.action === "documents") { navigate(`/client-workspace/${clientId}`); toast.info("Open the Documents tab to resolve missing document requests"); }
    else if (b.action === "ptf") { navigate(`/client-workspace/${clientId}`); toast.info("Open the Permanent Tax File to resolve pending proposals"); }
  };

  const draftReport = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/strategic-reviews/completion/${completion.id}/report/draft`, {});
      setReport(data.report);
      if (data.report?.status === "drafting") { schedulePoll(completion.id); toast.info("Allison is drafting the report — this can take a couple of minutes"); }
      setTimeout(() => reportRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Draft failed"); }
    finally { setBusy(false); }
  };

  const setSectionBody = (key, body) =>
    setReport((r) => ({ ...r, sections: r.sections.map((s) => (s.key === key ? { ...s, body } : s)) }));

  const saveSections = async () => {
    setSavingSections(true);
    try {
      await api.patch(`/strategic-reviews/report/${report.id}`, { sections: report.sections });
      toast.success("Report draft saved");
      const { data } = await api.get(`/strategic-reviews/completion/${completion.id}/report`);
      setReport(data.report);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed"); }
    finally { setSavingSections(false); }
  };

  const approveReport = async () => {
    setBusy(true);
    try {
      await api.post(`/strategic-reviews/report/${report.id}/approve`);
      toast.success("Management approved the report");
      await loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Approve failed"); }
    finally { setBusy(false); }
  };
  const deliverReport = async () => {
    setBusy(true);
    try {
      await api.post(`/strategic-reviews/report/${report.id}/deliver`);
      toast.success("Report delivered to the client");
      await loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Deliver failed"); }
    finally { setBusy(false); }
  };
  const reviseReport = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/strategic-reviews/completion/${completion.id}/report/revise`);
      setReport(data.report); toast.success(`Opened report v${data.report.version} for correction`);
      refreshReadiness();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Revise failed"); }
    finally { setBusy(false); }
  };

  const addAssumption = async () => {
    if (!aText.trim()) return;
    try {
      const { data } = await api.post(`/strategic-reviews/completion/${completion.id}/assumptions`, { text: aText, source: aSource });
      setAssumptions((l) => [...l, data.assumption]); setAText(""); setASource("");
      refreshReadiness();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not add assumption"); }
  };
  const approveAssumption = async (aid) => {
    try {
      await api.post(`/strategic-reviews/assumptions/${aid}/approve`);
      setAssumptions((l) => l.map((a) => (a.id === aid ? { ...a, status: "approved" } : a)));
      refreshReadiness();
      const { data } = await api.get(`/strategic-reviews/completion/${completion.id}/assumptions`);
      setAssumptions(data.assumptions || []);
    } catch { toast.error("Approve failed"); }
  };
  const removeAssumption = async (aid) => {
    try { await api.delete(`/strategic-reviews/assumptions/${aid}`); setAssumptions((l) => l.filter((a) => a.id !== aid)); refreshReadiness(); }
    catch { toast.error("Delete failed"); }
  };

  if (loading) return <LoadingState label="Loading Strategic Review readiness…" />;
  if (!published) return <EmptyState icon="Compass" title="No published review yet" description="Assemble a Strategic Review and use 'Make Available in Portal' first." />;
  if (!completion) return <EmptyState icon="Send" title="Not in the portal yet" description="Publish the assembled review so the client can complete it." />;
  if (!readiness) return <EmptyState icon="Gauge" title="Readiness unavailable" description="Waiting for the client to submit the questionnaire." />;

  const p = readiness.progress || {};
  const mgmt = p.management_review;
  const status = report?.status;
  const drafting = status === "drafting";
  const editable = report && ["draft", "management_edited", "draft_failed"].includes(status);

  return (
    <div className="space-y-5" data-testid="sr-readiness-report">
      {/* ───── Readiness ───── */}
      <SectionCard title="Strategic Review Readiness" icon="Gauge"
        action={<ActionButton variant="ghost" icon="RefreshCw" onClick={refreshReadiness} data-testid="sr-readiness-refresh">Refresh</ActionButton>}>
        {readiness.overall_ready ? (
          <AlertBanner tone="success" title="Ready for report drafting & delivery" data-testid="sr-readiness-ready">
            All deterministic gates are clear. This engagement can proceed to management approval and client delivery.
          </AlertBanner>
        ) : (
          <AlertBanner tone="warning" title={`${(readiness.blocking || []).length} blocking item(s) remaining`} data-testid="sr-readiness-blocked">
            Resolve every blocking item below. Readiness is computed deterministically — never by AI.
          </AlertBanner>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
          {["questionnaire", "documents", "supplemental", "permanent_tax_file"].map((k) => <Bar key={k} k={k} pct={p[k] ?? 0} />)}
        </div>
        <div className="flex items-center gap-2 mt-4 text-xs" data-testid="sr-progress-management_review">
          <span className="text-slate-600">Management Review:</span>
          <Pill tone={mgmt === "approved" ? "success" : mgmt === "drafted" ? "info" : "neutral"}>{mgmt || "pending"}</Pill>
        </div>

        {/* Blocking checklist */}
        <div className="mt-4 space-y-2" data-testid="sr-blocking-list">
          {(readiness.blocking || []).length === 0 ? (
            <div className="flex items-center gap-2 text-sm text-emerald-700"><Icons.CheckCircle2 className="h-4 w-4" />No blocking items.</div>
          ) : readiness.blocking.map((b) => {
            const I = Icons[BLOCK_ICON[b.type]] || Icons.AlertCircle;
            return (
              <button key={b.type} onClick={() => actOnBlock(b)} data-testid={`sr-block-${b.type}`}
                className="w-full flex items-center gap-3 text-left rounded-lg border border-rose-200 bg-rose-50 hover:bg-rose-100 px-3 py-2 transition-colors">
                <I className="h-4 w-4 text-rose-600 shrink-0" />
                <span className="text-sm text-rose-800 flex-1">{b.label}</span>
                <span className="text-[11px] text-rose-500 inline-flex items-center gap-1">Resolve <Icons.ArrowRight className="h-3 w-3" /></span>
              </button>
            );
          })}
        </div>
      </SectionCard>

      {/* ───── Report basis / evidence summary ───── */}
      {basis && (
        <SectionCard title="Report Basis & Evidence Summary" icon="Layers" data-testid="sr-report-basis">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <Stat label="Allison Run" value={basis.run ? `v${basis.run.run_version}` : "—"} sub={basis.run?.llm_model} />
            <Stat label="Analysis Profile" value={basis.run?.analysis_profile === "advanced_strategic_review" ? "Advanced" : basis.run ? "Standard" : "—"} sub={basis.run?.escalated ? "escalated" : null} />
            <Stat label="Questionnaire" value={basis.questionnaire?.version ? `v${basis.questionnaire.version}` : "submitted"} sub={basis.questionnaire?.submitted_at ? new Date(basis.questionnaire.submitted_at).toLocaleDateString() : null} />
            <Stat label="Documents" value={String(basis.documents_count)} sub="reviewed" />
            <Stat label="Permanent Tax File" value={`${basis.permanent_tax_file?.accepted_facts || 0} facts`} sub={basis.permanent_tax_file?.version ? `snapshot v${basis.permanent_tax_file.version}` : null} />
            <Stat label="Prior Organizers" value={String(basis.prior_organizers)} />
            <Stat label="Historical Conversations" value={String(basis.historical_conversations)} />
            <Stat label="Knowledge Center" value={basis.knowledge_center_as_of ? "as of" : "—"} sub={basis.knowledge_center_as_of ? new Date(basis.knowledge_center_as_of).toLocaleDateString() : null} />
            <Stat label="Approved Assumptions" value={String(basis.assumptions_approved)} />
            <Stat label="Draft Report" value={basis.report ? `v${basis.report.version}` : "not drafted"} sub={basis.report?.generated_at ? new Date(basis.report.generated_at).toLocaleDateString() : null} />
          </div>
          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Evidence sources considered</div>
            <div className="flex flex-wrap gap-1">
              {(basis.evidence_sources || []).map((s) => <Badge key={s} variant="secondary" className="text-[9px]"><Icons.Check className="h-2.5 w-2.5 mr-0.5 text-emerald-600" />{s}</Badge>)}
              <Badge variant="secondary" className="text-[9px]">Approved Assumptions</Badge>
            </div>
          </div>
        </SectionCard>
      )}

      {/* ───── Approved Assumptions register ───── */}
      <SectionCard title="Approved Assumptions Register" icon="ClipboardCheck" data-testid="sr-assumptions">
        <p className="text-xs text-slate-500 mb-3">Record assumptions the review relies on. Each carries a source and, once approved, the approving staff member. Approved assumptions are part of the report's evidence basis.</p>
        <div className="space-y-2 mb-3">
          {assumptions.length === 0 ? <p className="text-xs text-slate-300 italic">No assumptions recorded yet.</p> :
            assumptions.map((a) => (
              <div key={a.id} className="flex items-start gap-2 border border-slate-200 rounded-lg px-3 py-2" data-testid={`sr-assumption-${a.id}`}>
                <Pill tone={a.status === "approved" ? "success" : "neutral"}>{a.status}</Pill>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-800">{a.text}</div>
                  <div className="text-[11px] text-slate-400 mt-0.5">
                    Source: {a.source}
                    {a.status === "approved" && a.approved_by_name ? ` · Approved by ${a.approved_by_name}` : ` · Added by ${a.created_by_name || "staff"}`}
                  </div>
                </div>
                {a.status !== "approved" && (
                  <button onClick={() => approveAssumption(a.id)} className="text-[11px] text-emerald-600 hover:underline shrink-0 mt-1" data-testid={`sr-assumption-approve-${a.id}`}>Approve</button>
                )}
                <button onClick={() => removeAssumption(a.id)} className="text-rose-400 hover:text-rose-600 shrink-0 mt-0.5" data-testid={`sr-assumption-remove-${a.id}`}><Icons.X className="h-4 w-4" /></button>
              </div>
            ))}
        </div>
        <div className="flex flex-col sm:flex-row items-stretch gap-2">
          <Input value={aText} onChange={(e) => setAText(e.target.value)} placeholder="Assumption (e.g. Client remains a Thai tax resident for 2025)" className="flex-1" data-testid="sr-assumption-text" />
          <Input value={aSource} onChange={(e) => setASource(e.target.value)} placeholder="Source" className="sm:w-56" data-testid="sr-assumption-source" />
          <ActionButton variant="secondary" icon="Plus" onClick={addAssumption} data-testid="sr-assumption-add">Add</ActionButton>
        </div>
      </SectionCard>

      {/* ───── Report ───── */}
      <div ref={reportRef}>
        <SectionCard title="Strategic Review Report" icon="FileText" data-testid="sr-report-card"
          action={
            <div className="flex items-center gap-2">
              {report && <Badge variant="outline" className="text-[10px]">v{report.version} · {status}</Badge>}
              {!report && <ActionButton variant="primary" icon="Wand2" onClick={draftReport} loading={busy} data-testid="sr-report-draft">Draft with Allison</ActionButton>}
              {status === "draft_failed" && <ActionButton variant="primary" icon="RefreshCw" onClick={draftReport} loading={busy} data-testid="sr-report-retry">Retry Draft</ActionButton>}
              {editable && <ActionButton variant="secondary" icon="Save" onClick={saveSections} loading={savingSections} data-testid="sr-report-save">Save Draft</ActionButton>}
              {editable && <ActionButton variant="primary" icon="ShieldCheck" onClick={approveReport} loading={busy} disabled={!readiness.overall_ready} data-testid="sr-report-approve">Management Approve</ActionButton>}
              {status === "approved" && <ActionButton variant="primary" icon="Send" onClick={deliverReport} loading={busy} data-testid="sr-report-deliver">Deliver to Client</ActionButton>}
              {status === "delivered" && <ActionButton variant="secondary" icon="GitBranch" onClick={reviseReport} loading={busy} data-testid="sr-report-revise">Revise (new version)</ActionButton>}
            </div>
          }>
          {!report ? (
            <EmptyState icon="FileText" title="No report drafted yet"
              description="Allison drafts a structured report (Facts · Assumptions · Planning Opportunities · Open Questions) from her completed analysis. It stays a draft until management approves it." />
          ) : drafting ? (
            <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="sr-report-drafting">
              <Icons.Loader2 className="h-4 w-4 animate-spin" />Allison is drafting the report — this usually takes 1–3 minutes.
            </div>
          ) : (
            <>
              {status === "draft_failed" && (
                <AlertBanner tone="danger" title="Allison could not complete the draft">Use “Retry Draft”. You can still edit sections manually below.</AlertBanner>
              )}
              {editable && !readiness.overall_ready && (
                <AlertBanner tone="warning" title="Approval gated">Management approval is blocked until every deterministic readiness item is clear.</AlertBanner>
              )}
              {status === "approved" && (
                <AlertBanner tone="info" title="Management approved — ready to deliver">Delivery is an explicit staff action. Allison never delivers automatically.</AlertBanner>
              )}
              {status === "delivered" && (
                <AlertBanner tone="success" title="Delivered — this version is immutable">Corrections create a new version rather than altering the delivered copy. Use “Revise (new version)”.</AlertBanner>
              )}
              <div className="space-y-3 mt-3">
                {(report.sections || []).map((s) => (
                  <div key={s.key} className={`border border-slate-200 border-l-4 ${SECTION_ACCENT[s.key] || "border-l-slate-300"} rounded-lg p-3`} data-testid={`sr-report-section-${s.key}`}>
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <span className="text-sm font-medium text-slate-800">{s.title}</span>
                      {s.sources?.length > 0 && (
                        <div className="flex flex-wrap gap-1 justify-end" data-testid={`sr-section-sources-${s.key}`}>
                          {s.sources.map((src) => <Badge key={src} variant="secondary" className="text-[9px]">{src}</Badge>)}
                        </div>
                      )}
                    </div>
                    {editable ? (
                      <textarea rows={Math.max(3, (s.body || "").split("\n").length)} value={s.body || ""}
                        onChange={(e) => setSectionBody(s.key, e.target.value)}
                        className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm font-mono text-slate-700"
                        data-testid={`sr-section-body-${s.key}`} placeholder="No content drafted for this section." />
                    ) : (
                      <p className="text-sm text-slate-600 whitespace-pre-wrap">{s.body || <span className="italic text-slate-300">No content.</span>}</p>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </SectionCard>
      </div>
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="rounded-lg bg-slate-50 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className="text-sm font-medium text-slate-800">{value}</div>
      {sub && <div className="text-[10px] text-slate-400">{sub}</div>}
    </div>
  );
}
